import { useState, useCallback, useMemo, useEffect } from "react"
import { MultiSelectOption } from "../types"

interface UseMultiSelectProps {
  options: MultiSelectOption[]
  value?: string[]
  onChange?: (selectedValues: string[]) => void
  maxItems?: number
  searchable?: boolean
  closeOnSelect?: boolean
}

export const useMultiSelect = ({
  options,
  value = [],
  onChange,
  maxItems,
  searchable = true,
  closeOnSelect = false,
}: UseMultiSelectProps) => {
  const [selectedValues, setSelectedValues] = useState<string[]>(value)
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [focusedIndex, setFocusedIndex] = useState(-1)

  // 外部からのvalue変更を反映
  useEffect(() => {
    setSelectedValues(value)
  }, [value])

  // フィルタされたオプション
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchValue) return options

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
        option.value.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [options, searchValue, searchable])

  // 選択されたオプション情報
  const selectedOptions = useMemo(() => {
    return selectedValues
      .map((value) => options.find((option) => option.value === value))
      .filter(Boolean) as MultiSelectOption[]
  }, [selectedValues, options])

  // オプション選択/選択解除
  const toggleOption = useCallback(
    (optionValue: string) => {
      const newSelectedValues = selectedValues.includes(optionValue)
        ? selectedValues.filter((value) => value !== optionValue)
        : maxItems && selectedValues.length >= maxItems
        ? selectedValues
        : [...selectedValues, optionValue]

      setSelectedValues(newSelectedValues)
      onChange?.(newSelectedValues)

      if (closeOnSelect && !selectedValues.includes(optionValue)) {
        setIsOpen(false)
        setSearchValue("")
        setFocusedIndex(-1)
      }
    },
    [selectedValues, onChange, maxItems, closeOnSelect]
  )

  // タグの削除
  const removeValue = useCallback(
    (valueToRemove: string) => {
      const newSelectedValues = selectedValues.filter((value) => value !== valueToRemove)
      setSelectedValues(newSelectedValues)
      onChange?.(newSelectedValues)
    },
    [selectedValues, onChange]
  )

  // 全削除
  const clearAll = useCallback(() => {
    setSelectedValues([])
    onChange?.([])
  }, [onChange])

  // 検索値の更新
  const updateSearchValue = useCallback((value: string) => {
    setSearchValue(value)
    setFocusedIndex(-1) // 検索時はフォーカスをリセット
  }, [])

  // ドロップダウンの開閉
  const openDropdown = useCallback(() => {
    setIsOpen(true)
  }, [])

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
    setSearchValue("")
    setFocusedIndex(-1)
  }, [])

  const toggleDropdown = useCallback(() => {
    if (isOpen) {
      closeDropdown()
    } else {
      openDropdown()
    }
  }, [isOpen, closeDropdown, openDropdown])

  // フォーカスされたオプションで選択実行
  const selectFocusedOption = useCallback(() => {
    if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
      const option = filteredOptions[focusedIndex]
      if (!option.disabled) {
        toggleOption(option.value)
      }
    }
  }, [focusedIndex, filteredOptions, toggleOption])

  // 最大選択数に達しているかチェック
  const isMaxReached = useMemo(() => {
    return maxItems ? selectedValues.length >= maxItems : false
  }, [maxItems, selectedValues.length])

  return {
    // State
    selectedValues,
    selectedOptions,
    isOpen,
    searchValue,
    focusedIndex,
    filteredOptions,
    isMaxReached,

    // Actions
    toggleOption,
    removeValue,
    clearAll,
    updateSearchValue,
    openDropdown,
    closeDropdown,
    toggleDropdown,
    selectFocusedOption,
    setFocusedIndex,
  }
}
